/**
 * act-as-nav — may this surface offer a link into the client's own screens?
 *
 * ONE PREDICATE, NOT A FOURTH LIST. `route-policy.ts` owns the four paths that
 * belong to the client and to nobody standing in for them, and the /app route
 * guard already refuses them. `firm-act-store.ts` owns the posture: is somebody
 * standing in for somebody else right now. This module is the two of them read
 * together, so every surface that offers a link asks the same question the guard
 * answers.
 *
 * WHY IT EXISTS. Until 2026-08-27 the answer was computed in the SIDEBAR ONLY.
 * Three other surfaces went on offering the same four destinations to a firm
 * acting as a client, and every one of them dead-ended: the guard bounced the
 * click back to /app/dashboard, which is where two of the links were rendered,
 * so the firm was returned to the page that invited them into the wall.
 *
 *   components/dashboard/hero.tsx        Ask them (/app/inbox) and
 *                                        Connect your fiduciary firm
 *                                        (/app/firm-access), both on
 *                                        /app/dashboard, the bounce target.
 *   routes/app/profile-screen.tsx        Security Centre (/app/security) and
 *                                        Privacy Centre (/app/privacy).
 *                                        /app/profile is not client-only, so a
 *                                        firm acting as a client reaches it.
 *   components/screens/screen-shell.tsx  an unread-message notification in the
 *                                        bell, resolving to /app/inbox.
 *
 * Paul, 25 August 19:31: "I actually asked AI to remove the fact that you as an
 * administrator can access the privacy, can see security and inbox. I just would
 * want them to see the trusts and the overview."
 *
 * HIDING A LINK IS NOT SECURITY, AND THIS IS NOT THE SECURITY LAYER. The guard
 * at `routes/app/_layout.tsx` is, and the server-side privacy scopes are. This
 * only stops the product from offering a door it will then slam.
 *
 * `client-only-surfaces.test.tsx` renders all three surfaces under act-as and
 * fails if any of them still paints one of the four, so a fifth surface cannot
 * appear unnoticed.
 */

import { useCallback } from 'react';
import { useIsActingAsClient } from '../acting/context';
import { isClientOnlyPath } from './route-policy';

/**
 * Returns a predicate: given a destination path, is it hidden from whoever is
 * at the keyboard right now? True only when a firm is acting as a client AND
 * the destination is one of the client's own screens.
 *
 * Read from the acting context's POSTURE rather than from the role, because
 * the act-as token deliberately carries role 'admin' so the firm can do the
 * work it was granted. Posture is the fact that matters here, not the
 * permission tier (ARC-01: and never the store directly).
 */
export function useHiddenFromActingFirm(): (to: string) => boolean {
  const isActingAsClient = useIsActingAsClient();
  return useCallback(
    (to: string) => isActingAsClient && isClientOnlyPath(to),
    [isActingAsClient],
  );
}
